/* eslint-disable react-hooks/exhaustive-deps */
// Disable exhaustive deps as the deps configuration of the useEffect hooks are calibrated
// to ensure that they're not run for nothing.

import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import omit from "lodash/omit";
import some from "lodash/some";

import Select from "../Select/Select";
import findItemWithMatchingChildValue from "../Utils/findItemWithMatchingChildValue";
import { useFieldContext } from "../Form/Field";
import { useInputAriaDescribedBy } from "../Form/useInputAriaDescribedBy";

const TreeSelectInput = props => {
  const {
    value,
    formKey,
    fieldName,
    options,
    childSelectsClassName,
    onChange,
    ariaDescribedBy: providedAriaDescribedBy
  } = props;

  const inputLevel = props.inputLevel || 0;
  const inputName = [fieldName, inputLevel].join("-");

  const { fieldId } = useFieldContext();
  // Avoid duplicating the input id, as only the first select of the multiple select
  // inputs is assigned a label to.
  const inputId = inputLevel === 0 ? fieldId : null;

  // The TreeSelectInput maintains a local state of the currently selected item for each
  // input level, and doesn't share it with the form. This state is used to handle the
  // currently selected option in the component's <Select/>.
  //
  const [currentValue, setCurrentValue] = useState(null);
  const currentValueWasUpdated = useRef(false);

  const contextualAriaDescribedBy = useInputAriaDescribedBy();
  const ariaDescribedBy =
    typeof providedAriaDescribedBy !== "undefined"
      ? providedAriaDescribedBy
      : contextualAriaDescribedBy;

  // When the component is mounted, we process the initial provided value to make
  // the current view state match the currently selected value.
  //
  useEffect(() => {
    // The react-select components accepts a { value, label } option object as
    // its value prop, so we ensure that we have an option object to pass to
    // the component.
    const selectedValue = props.value;
    const value =
      (selectedValue.value &&
        !selectedValue.label &&
        findItemWithMatchingChildValue(options, selectedValue.value)) ||
      // Make empty value be `null` and not `{ value: null }` to ensure that the
      // placeholder is shown when empty
      (selectedValue.value?.length && selectedValue) ||
      null;

    // Define the initial `currentValue` variable. We cannot do this outside of the
    // `useEffect` mounting hook as it creates an infinite update/render loop of the
    // component
    setCurrentValue(value);

    // Flag the currentValue as just updated
    currentValueWasUpdated.current = true;
  }, []);

  useEffect(() => {
    // Avoid removing the currentValue when the above hook just set it in the same
    // component function call, as the `currentValue` isn't already set with its new
    // value before the component is rendered again
    if (currentValueWasUpdated.current) {
      currentValueWasUpdated.current = false;
      return;
    }

    // Ensure that the last selected value still exist in the options list
    const valueExistsInOptions = some(
      options,
      option => option.value === currentValue?.value
    );

    if (!valueExistsInOptions && currentValue?.value !== null) {
      setCurrentValue(null);
      // Since we're resetting a subtree of the input, the final value can't be still
      // selected, so we also reset the value of the whole input tree so the redux form
      // state knows we have no real value selected.
      onChange(null);
    }
  }, [currentValue, options]);

  // Force null value to avoid "0" to be shown in view by JSX
  const currentValueHasChildren = currentValue?.children?.length || null;

  const handleOptionChanged = option => {
    setCurrentValue(option);

    // Do not propagate selected value if it's not a final node value selection
    if (option?.children?.length) return;

    // Tell the containing input that the value was changed
    if (onChange) {
      onChange(option);
    }
  };

  const className = classNames(["tree-select-input", "react-select", props.className]);

  const passtroughProps = omit(props, [
    "value",
    "fieldName",
    "formKey",
    "onChange",
    "className"
  ]);

  return (
    <>
      <Select
        inputId={inputId}
        className={className}
        classNamePrefix="react-select"
        name={inputName}
        value={currentValue}
        options={options}
        onChange={handleOptionChanged}
        styles={{ menu: provided => ({ ...provided, zIndex: 2000 }) }}
        aria-describedby={ariaDescribedBy}
        {...passtroughProps}
      />

      {currentValueHasChildren && (
        <TreeSelectInput
          {...{ formKey, fieldName }}
          options={currentValue.children}
          value={value}
          inputLevel={inputLevel + 1}
          className={childSelectsClassName}
          childSelectsClassName={childSelectsClassName}
          onChange={onChange}
          ariaDescribedBy={null}
        />
      )}
    </>
  );
};

export default TreeSelectInput;
